'use client';

import { useEffect } from 'react';
import { renewalDueAt, SESSION_RENEWAL_INIT, SESSION_RENEWAL_URL } from '@/lib/session-lifetime';

/** How often a visible page looks at the clock, and the least time between two
 *  renewal requests of one tab (a client clock ahead of the server's asks
 *  early, and the server answers without renewing). */
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** When this tab last asked; module state, so a re-rendered layout that hands
 *  down the same stale expiry does not ask again at once. */
let lastAsked = 0;

/**
 * Renews the viewer's session while the person uses the app
 * (src/lib/session-lifetime.ts). The server's page reads never renew it,
 * because a server component cannot write the cookie, so this asks the auth
 * route: a POST to `/get-session` extends the row and sets the cookie in one
 * response, when the session is due. It asks only while the page is visible,
 * on load, when the page comes back, and once an hour, and only once the
 * expiry the server handed down says a renewal is due. Renders nothing.
 */
export function SessionRenewal({ expiresAt }: { expiresAt: number }) {
  useEffect(() => {
    let expiry = expiresAt;
    let cancelled = false;

    const renewIfDue = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now < renewalDueAt(expiry) || now - lastAsked < CHECK_INTERVAL_MS) return;
      lastAsked = now;
      void renewSession().then((renewed) => {
        if (!cancelled && renewed !== null) expiry = renewed;
      });
    };

    renewIfDue();
    document.addEventListener('visibilitychange', renewIfDue);
    const timer = setInterval(renewIfDue, CHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', renewIfDue);
      clearInterval(timer);
    };
  }, [expiresAt]);

  return null;
}

/** Ask the auth route to renew the session; the new expiry, or `null` when
 *  there is no session or the request failed. */
async function renewSession(): Promise<number | null> {
  try {
    const res = await fetch(SESSION_RENEWAL_URL, { ...SESSION_RENEWAL_INIT, cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { session?: { expiresAt?: string } } | null;
    const renewed = Date.parse(body?.session?.expiresAt ?? '');
    return Number.isFinite(renewed) ? renewed : null;
  } catch {
    return null;
  }
}
